import type { TreePath } from '@st/util/tree-expansion'
import { checklistRows } from '~/features/checklists/selectors'
import { match, P } from '@st/pattern'
import { paginateChecklistItems } from '@st/formation'
import { evalFolderExpr } from '@st/formation'
import {
  selActiveAnnotation,
  selAnnotations,
  selAnnotationsForPage,
  selAnnotationsPluginState
} from '@st/document-viewer-kit/plugins/annotations'
import {
  ActivityFeedEvent,
  ClientOrganizerSettings,
  Entities,
  DocumentWarning,
  DocumentWarningList,
  MoveChecklistItem,
  ReorderDocument,
  ScopedFolderMembership,
  SetChecklistItemVisibility,
  STCategory,
  STChecklistItem,
  STDocument,
  STDocumentType,
  STOpenFolderState,
  STMissingReason,
  StampAnnotation
} from '@st/sdk'
import { UNCATEGORIZED_DOCUMENT_TYPE_ID } from '@st/ui-config'
import { findLast } from '@st/util/array'
import { setAdd, setRemove } from '@st/util/array-set'
import { isNotEmpty } from '@st/util/json-value'
import { Section } from '@st/util/section'
import { asc, rank, sort } from '@st/util/sort'
import { isValidInvoiceUrl } from './document-viewer-extensions/tax-return-delivery/invoice-link'
import { formatISO8601Date, formatISO8601DateTime, formatISO8601Time } from '@st/util/time'
import { formatDocumentWarning } from './document-warnings'
import { folderMembershipSide, type FolderMembershipSide } from './folder-membership-side'
import type { FolderQuestionnaireDashboardState } from './st-folder-questionnaire-types'
import {
  isOptimisticOperationItem,
  operationStatus,
  optimisticReduce,
  optimisticRequestsByType
} from './operation-request-selectors'
import { resolveViewerDocKind } from '~/st-doc-viewer/viewer-document-model'
import type { SidebarVisibility } from '~/st-doc-viewer/sidebar-layout'
import type { STViewerAnnotation } from '~/st-doc-viewer/viewer-annotations'
import { navigateToAnnotation } from './st-folder-focus'
import type {
  AuthorizedOpenFolderState,
  FolderRequest,
  NavigationRequest,
  STFolderModuleState,
  STFolderReadyState
} from './st-folder-types'
export { selResolvedInputs } from './st-folder-state-selectors'
import { selFormationLayout, selLoadedFormationConfig } from './st-formation-selectors'

export function selFolderReadyState(state: STFolderModuleState): STFolderReadyState | undefined {
  if (state.status != 'ready') return undefined
  return state
}

/** Current visibility owned by the folder model. */
export function selSidebarVisibility(state: STFolderModuleState): SidebarVisibility {
  if (state.status != 'ready') throw new Error('Sidebar visibility requires a ready folder')
  return state.sidebarVisibility
}

/** The field tool alone does not show fields when the sidebar is collapsed. */
export function selFieldsSidebarShown(state: STFolderModuleState): boolean {
  return (
    selAnnotationsPluginState(state.viewer).selectedToolName == 'field' &&
    selSidebarVisibility(state) == 'expanded'
  )
}

export function selFolderState(state: STFolderModuleState): AuthorizedOpenFolderState | null {
  return state.status == 'ready' ? state.folderState : null
}

export function selFolderId(state: STFolderModuleState): string | null {
  const folderState = selFolderState(state)
  return folderState ? folderState.folderId : null
}

export function selFolder(state: STFolderModuleState) {
  const folderState = selFolderState(state)
  return folderState ? folderState.folder : null
}

export function selFolderDocuments(state: STFolderModuleState): STDocument[] {
  if (state.status != 'ready') return []
  return projectDocuments(state, state.folderState.documents)
}

function projectDocuments(state: STFolderReadyState, base: STDocument[]): STDocument[] {
  const reorderedDocuments = optimisticReduce(state.requestQueue.items, {
    base,
    type: 'folders/reorderDocument',
    reduce: reorderDocuments
  })

  const documents = reorderedDocuments.map((doc) =>
    optimisticEntity({
      base: doc,
      queueItems: state.requestQueue.items,
      filter: (req) => {
        if (!('documentId' in req)) return false
        return req.documentId == doc.id
      },
      reduce: optimisticDocument
    })
  )

  if (state.folderViewMode == 'organization_preview_questionnaire') {
    return documents.filter(isClientVisibleDocument)
  }

  return documents
}

export function selFolderMissingReasonOptionsByConstraintId(
  state: STFolderModuleState
): Record<string, STMissingReason[]> {
  if (state.status != 'ready') return {}
  return selMissingReasonOptionsListByConstraintId(state.folderState)
}
export function selFolderBookmarkSections(
  state: STFolderModuleState
): Section<STDocumentType, STDocument>[] {
  if (state.status != 'ready') return []
  return getDocumentSections({
    documents: selFolderDocuments(state),
    documentTypes: state.folderState.documentTypes
  })
}

export function selMembershipSide(state: STFolderModuleState): FolderMembershipSide {
  if (state.status != 'ready') return 'client'
  if (!state.currentUserId) return 'client'
  return folderMembershipSide(state.folderState.memberships, state.currentUserId) ?? 'client'
}

export function selFolderUsers(state: STFolderModuleState) {
  if (state.status != 'ready') return []
  return state.folderState.users
}

export function selViewerAnnotationState(state: STFolderModuleState) {
  return selAnnotationsPluginState(state.viewer)
}

export function selViewerActiveAnnotation(state: STFolderModuleState) {
  return selActiveAnnotation(state.viewer) as STViewerAnnotation | undefined
}

export function selViewerAnnotationsForPage(state: STFolderModuleState, pageNumber: number) {
  return selAnnotationsForPage(state.viewer, pageNumber) as STViewerAnnotation[]
}

export function selViewerStampAnnotations(state: STFolderModuleState): StampAnnotation[] {
  return Object.values(
    selAnnotations(
      state.viewer,
      (annotation): annotation is StampAnnotation => annotation.type == 'stamp'
    )
  )
}

export type ExpandedActivityFeedEvent = ActivityFeedEvent & {
  actorName?: string
  actorSide?: FolderMembershipSide
  document?: STDocument
}

export function selActivityFeedEvents(state: STFolderModuleState): ExpandedActivityFeedEvent[] {
  if (state.status != 'ready') return []
  const folderState = state.folderState
  const documents = [...folderState.documents, ...folderState.deletedDocuments]

  return folderState.activityFeed.events.map((event) => {
    const actor = folderState.memberships.find((membership) => membership.user.id == event.actorId)

    return {
      ...event,
      actorName: actor?.user.name || actor?.user.email,
      actorSide: folderMembershipSide(folderState.memberships, event.actorId),
      document: resolveActivityFeedDocument(documents, event)
    }
  })
}

function resolveActivityFeedDocument(
  documents: STDocument[],
  event: ActivityFeedEvent
): STDocument | undefined {
  if (!('documentId' in event)) return undefined
  return documents.find((document) => document.id == event.documentId)
}

export function selQuestionnaireDocumentsOfType(
  state: STFolderModuleState,
  documentTypeId: string
) {
  if (state.status != 'ready') return []
  const folderState = state.folderState
  const documentType = folderState.documentTypes.find((dt) => dt.id == documentTypeId)

  if (!documentType) return []

  return folderState.documents.filter((doc) => {
    const filterDocumentTypeId = doc.questionnaireDocumentTypeId
    if (filterDocumentTypeId == undefined) return false
    if (documentType.id == filterDocumentTypeId) return true

    if (
      documentType.possibleEmbeddedDocumentTypeIds &&
      documentType.possibleEmbeddedDocumentTypeIds.includes(filterDocumentTypeId)
    ) {
      return true
    }
    return false
  })
}

export function selDocumentTypeOptions(state: STFolderModuleState) {
  if (state.status != 'ready') return []
  return sort(
    state.folderState.documentTypes.filter((dt) => dt.id != 'stanfordtax'),
    asc('name')
  )
}

type FolderRequestType = FolderRequest['type']
type FolderRequestOf<T extends FolderRequestType> = Extract<FolderRequest, { type: T }>
type RequestQueueItem = STFolderReadyState['requestQueue']['items'][number]

export function selOptimisticReduce<Base, T extends FolderRequestType>(
  state: STFolderModuleState,
  params: {
    base: Base
    type: T
    filter?: Partial<Omit<FolderRequestOf<T>, 'type'>>
    reduce: (base: Base, request: FolderRequestOf<T>) => Base
  }
) {
  if (state.status != 'ready') return params.base
  return optimisticReduce(state.requestQueue.items, params)
}

export function selClientComment(state: STFolderModuleState, clientCommentKey: string) {
  if (state.status != 'ready') return undefined

  const commands = optimisticRequestsByType(state.requestQueue.items, {
    type: 'folders/setClientComment',
    filter: { commentKey: clientCommentKey }
  })

  const latestCommand = commands[commands.length - 1]

  if (latestCommand) {
    return latestCommand.commentBody
  }
  return state.folderState.comments.find((comment) => comment.anchor == clientCommentKey)?.content
}

export function selReadInput(state: STFolderModuleState, key: string) {
  if (state.status != 'ready') return undefined

  const commands = optimisticRequestsByType(state.requestQueue.items, {
    type: 'folders/setFolderInputs'
  })

  const cmd = findLast(commands, (latestCommand) => key in latestCommand.inputs)

  return cmd ? cmd.inputs[key] : state.folderState.inputs[key]
}

export function selEvalFolderStateExpr(state: STFolderModuleState, expr: string | undefined) {
  if (state.status != 'ready') return false
  return evalFolderExpr(expr, {
    inputs: state.folderState.inputs,
    priorInputs: state.folderState.prevInputs,
    settings: state.folderState.settings,
    context: {}
  })
}

export function selFolderOperationInProgress(
  state: STFolderModuleState,
  type: FolderRequest['type']
) {
  return selOperationStatus(state, { type }) == 'running'
}

export function selOperationStatus<T extends FolderRequestType>(
  state: STFolderModuleState,
  filter: { type: T } & Partial<Omit<FolderRequestOf<T>, 'type'>>
): 'idle' | 'running' {
  if (state.status != 'ready') return 'idle'
  return operationStatus(state.requestQueue.items, filter)
}

export function selChecklistItems(state: STFolderModuleState) {
  if (state.status != 'ready') return []
  const folderState = state.folderState
  const baseChecklistItems = folderState.checklistItems

  const pending = state.requestQueue.items
    .filter(isOptimisticOperationItem)
    .map((i) => i.op.request)
    .filter(
      (req): req is MoveChecklistItem | SetChecklistItemVisibility =>
        req.type == 'folders/moveChecklistItem' || req.type == 'folders/setChecklistItemVisibility'
    )

  if (pending.length == 0) {
    return baseChecklistItems
  }

  return pending.reduce((items, req) => {
    switch (req.type) {
      case 'folders/moveChecklistItem': {
        const optimisticChecklistItems = [...items]
        const { checklistItemId, relation, referenceChecklistItemId } = req
        const itemIndex = optimisticChecklistItems.findIndex((item) => item.id == checklistItemId)
        if (itemIndex == -1) return items
        const [itemToMove] = optimisticChecklistItems.splice(itemIndex, 1)

        let referenceIndex = -1
        if (referenceChecklistItemId) {
          referenceIndex = optimisticChecklistItems.findIndex(
            (item) => item.id == referenceChecklistItemId
          )
        }

        let targetIndex: number
        switch (relation) {
          case 'before':
            targetIndex = referenceIndex >= 0 ? referenceIndex : optimisticChecklistItems.length
            break
          case 'after':
            targetIndex = referenceIndex >= 0 ? referenceIndex + 1 : optimisticChecklistItems.length
            break
          case 'start_of_list':
            targetIndex = 0
            break
          case 'end_of_list':
            targetIndex = optimisticChecklistItems.length
            break
          default:
            targetIndex = optimisticChecklistItems.length
            break
        }

        optimisticChecklistItems.splice(targetIndex, 0, itemToMove)
        return optimisticChecklistItems
      }
      case 'folders/setChecklistItemVisibility': {
        if (req.folderId != folderState.folderId) return items
        const idx = items.findIndex((item) => item.id == req.checklistItemId)
        if (idx == -1) return items
        const cur = items[idx]
        if (cur.visibility == req.visibility) return items
        const next = [...items]
        next[idx] = { ...cur, visibility: req.visibility }
        return next
      }
    }
  }, baseChecklistItems)
}

export function selDocumentChecklistItems(state: STFolderModuleState) {
  return selChecklistItems(state).filter(
    (item) => item.phase == 'intake' && item.context.type == 'document_request'
  )
}

/**
 * Firm preview should match what clients actually see.
 * Backend permissions are the real gate; this is only a preview guardrail so
 * accountants do not panic when they see internal items that clients cannot access.
 */
export function selClientPerspectiveChecklistItems(state: STFolderModuleState) {
  return selChecklistItems(state).filter(
    (item) => item.visibility == undefined || item.visibility == 'all'
  )
}

export function selClientPerspectiveDocumentChecklistItems(state: STFolderModuleState) {
  return selClientPerspectiveChecklistItems(state).filter(
    (item) => item.phase == 'intake' && item.context.type == 'document_request'
  )
}

export function selClientPerspectiveNonDocumentChecklistItems(state: STFolderModuleState) {
  return selClientPerspectiveChecklistItems(state).filter(
    (item) => item.phase == 'intake' && item.context.type == 'questionnaire_entry'
  )
}

const CLIENT_PERSPECTIVE_HIDDEN_DOCUMENT_TYPE_IDS = new Set([
  'stanfordtax',
  'engagement_letter',
  'internal'
])

export function canToggleDocumentVisibility(doc: STDocument) {
  return !CLIENT_PERSPECTIVE_HIDDEN_DOCUMENT_TYPE_IDS.has(doc.documentTypeId ?? '')
}

/**
 * Same idea as selClientPerspectiveChecklistItems: hide internal docs in preview.
 */
export function selClientPerspectiveDocuments(state: STFolderModuleState) {
  const documents = selFolderDocuments(state)
  return documents.filter(isClientVisibleDocument)
}

export function selClientPerspectiveDocumentsUnlinkedToChecklistItems(state: STFolderModuleState) {
  const documents = selClientPerspectiveDocuments(state)
  if (state.status != 'ready') return documents
  const checklistItems = state.folderState.checklistItems
  return documents.filter(
    (doc) =>
      !checklistItems.some((item) =>
        item.attachments.some((attachment) => attachment.documentId == doc.id)
      )
  )
}

export function selDocumentWarnings(
  state: STFolderModuleState
): Record<string, DocumentWarningList> {
  if (state.status != 'ready') return {}
  return state.folderState.documentWarnings
}

export const selSidebarDocuments = selFolderDocuments
export const selSidebarDocumentWarnings = selDocumentWarnings

export function selNonStanfordtaxBookmarkSections(state: STFolderModuleState) {
  return selFolderBookmarkSections(state).filter((section) => section.heading.id != 'stanfordtax')
}

export function selIncompleteChecklistItems(state: STFolderModuleState) {
  if (state.status != 'ready') return []
  return state.folderState.checklistItems.filter((item) => item.status == 'incomplete')
}

export function selFolderSaveStatus(state: STFolderModuleState): 'saving' | 'saved' {
  if (state.status != 'ready') return 'saved'
  const runningOps = state.requestQueue.items.filter((s) => s.status == 'running')
  return runningOps.length > 0 ? 'saving' : 'saved'
}

export function selAlreadySplitDocuments(state: STFolderModuleState, docId: string) {
  if (state.status != 'ready') return []
  return state.folderState.documents.filter(
    (doc) => doc.source?.documentId == docId && doc.source?.operation == 'split'
  )
}

export function hasUploadedDocuments(documents: STDocument[]) {
  return documents.some((d) => d.documentTypeId != 'stanfordtax')
}

export function getDocumentSections(state: {
  documentTypes: STDocumentType[]
  documents: STDocument[]
}): Section<STDocumentType, STDocument>[] {
  const sections: Section<STDocumentType, STDocument>[] = []
  for (const dt of state.documentTypes) {
    const section = {
      heading: dt,
      items: state.documents.filter(
        (d) =>
          (d.documentTypeId ?? UNCATEGORIZED_DOCUMENT_TYPE_ID) == dt.id &&
          d.processingStatus != 'processing'
      )
    }
    if (section.items.length > 0) {
      sections.push(section)
    }
  }

  const processingDocumentsSection: Section<STDocumentType, STDocument> = {
    heading: {
      id: 'processing',
      name: 'Processing...',
      expectedLate: false,
      missingReasons: [],
      pageGroupTypes: [],
      possibleEmbeddedDocumentTypeIds: []
    },
    items: state.documents.filter((d) => d.processingStatus == 'processing')
  }
  if (processingDocumentsSection.items.length > 0) {
    sections.push(processingDocumentsSection)
  }

  const invalidCategoriesSection: Section<STDocumentType, STDocument> = {
    heading: {
      id: 'invalid_category',
      name: 'Invalid Category',
      expectedLate: false,
      missingReasons: [],
      pageGroupTypes: [],
      possibleEmbeddedDocumentTypeIds: []
    },
    items: state.documents.filter((doc) => {
      for (const section of sections) {
        if (section.items.find((el) => el.id == doc.id)) {
          return false
        }
      }
      return true
    })
  }
  if (invalidCategoriesSection.items.length > 0) {
    sections.push(invalidCategoriesSection)
  }

  return sections
}

export function selExpandedPaths(state: STFolderModuleState): TreePath[] {
  if (state.status != 'ready') return []
  return state.expandedPaths
}

export function selSelectedDoc(state: STFolderModuleState) {
  if (state.status != 'ready') return undefined
  if (!state.selectedDocId) return undefined
  return selFolderDocuments(state).find((d) => d.id == state.selectedDocId)
}

export type CrossReferenceNavigationState = {
  prev?: NavigationRequest
  next?: NavigationRequest
}

export function selActiveCrossReferenceNavigationState(state: STFolderModuleState) {
  const annotation = selViewerActiveAnnotation(state)
  if (annotation?.type != 'stamp') return undefined
  return selCrossReferenceNavigationState(state, annotation)
}

export function selCrossReferenceNavigationState(
  state: STFolderModuleState,
  anchorAnnotation: StampAnnotation
): CrossReferenceNavigationState | undefined {
  if (state.status != 'ready') return undefined
  if (anchorAnnotation.stampType != 'cross_reference') return undefined

  const orderedAnnotations = selFolderBookmarkSections(state)
    .flatMap((section) => section.items)
    .flatMap((doc, documentIndex) =>
      selNavigableCrossReferenceAnnotations(state, doc)
        .filter((annotation) => annotation.stampCode == anchorAnnotation.stampCode)
        .map((annotation) => ({
          documentId: doc.id,
          documentIndex,
          annotation
        }))
    )
    .toSorted((a, b) => {
      if (a.documentIndex != b.documentIndex) return a.documentIndex - b.documentIndex
      if (a.annotation.pageNumber != b.annotation.pageNumber) {
        return a.annotation.pageNumber - b.annotation.pageNumber
      }
      if (a.annotation.bounds.y != b.annotation.bounds.y) {
        return a.annotation.bounds.y - b.annotation.bounds.y
      }
      if (a.annotation.bounds.x != b.annotation.bounds.x) {
        return a.annotation.bounds.x - b.annotation.bounds.x
      }
      return a.annotation.id.localeCompare(b.annotation.id)
    })

  const anchorIndex = orderedAnnotations.findIndex(
    (entry) => entry.annotation.id == anchorAnnotation.id
  )

  if (anchorIndex == -1) return undefined

  const prev = orderedAnnotations[anchorIndex - 1]
  const next = orderedAnnotations[anchorIndex + 1]

  return {
    prev: prev && navigateToAnnotation(prev.documentId, prev.annotation.id),
    next: next && navigateToAnnotation(next.documentId, next.annotation.id)
  }
}

export type FolderViewerHeader = {
  folderLoaded: boolean
  folderType: string | undefined
  organizationSlug: string | undefined
  folderEntities: Entities | undefined
  folderTagIds: string[]
  clientOrganizerStatus: STOpenFolderState['folder']['clientOrganizer']['status'] | undefined
  questionnaireProgress: STOpenFolderState['folder']['clientOrganizer']['questionnaireProgress']
  isArchived: boolean
  sourceName: string | undefined
  sourceId: string | undefined
  taxReturnId: string | undefined
  taxReturnLastPulledAt: string | undefined
  taxReturnRefreshStatus: 'idle' | 'running'
}

export function selFolderViewerHeader(state: STFolderModuleState): FolderViewerHeader {
  const readyState = selFolderReadyState(state)
  const folder = readyState?.folderState.folder

  return {
    folderLoaded: folder != undefined,
    folderType: folder?.type,
    organizationSlug: readyState?.folderState.organization.slug,
    folderEntities: folder?.entities,
    folderTagIds: folder?.tagIds ?? [],
    clientOrganizerStatus: folder?.clientOrganizer.status,
    questionnaireProgress: folder?.clientOrganizer.questionnaireProgress ?? 0,
    isArchived: folder?.archived == true,
    sourceName: folder?.sourceName,
    sourceId: folder?.sourceId,
    taxReturnId: folder?.taxReturnId,
    taxReturnLastPulledAt: readyState?.folderState.taxReturnLastPulledAt,
    taxReturnRefreshStatus: folder?.taxReturnId
      ? selOperationStatus(state, {
          type: 'taxReturns/pullTaxReturn',
          taxReturnId: folder.taxReturnId
        })
      : 'idle'
  }
}

export function selBulkUpload(state: STFolderModuleState) {
  return state.status == 'ready' ? state.bulkUpload.dialog : undefined
}

function isClientVisibleDocument(doc: STDocument) {
  return (
    doc.documentTypeId != 'tax_return_packet' &&
    (doc.visibility == undefined || doc.visibility == 'all') &&
    canToggleDocumentVisibility(doc)
  )
}

function selNavigableCrossReferenceAnnotations(
  state: Extract<STFolderModuleState, { status: 'ready' }>,
  doc: STDocument
) {
  const annotations = Object.values(doc.annotations).filter(
    (annotation): annotation is StampAnnotation =>
      annotation.type == 'stamp' && annotation.stampType == 'cross_reference'
  )

  if (annotations.length == 0) {
    return []
  }

  const docKind = resolveViewerDocKind(doc.mimeType)

  switch (docKind) {
    case 'formation-checklist':
      return []
    case 'formation': {
      const formationConfig = selLoadedFormationConfig(state)
      if (!formationConfig) {
        return annotations.filter(
          (annotation) => Number.isInteger(annotation.pageNumber) && annotation.pageNumber > 0
        )
      }

      const visiblePageCount = selFormationLayout(state).pages.length

      return annotations.filter(
        (annotation) =>
          Number.isInteger(annotation.pageNumber) &&
          annotation.pageNumber > 0 &&
          annotation.pageNumber <= visiblePageCount
      )
    }
    case 'none':
      return []
    case 'pdf':
    default: {
      const deletedPageNumbers = new Set(doc.deletedPageNumbers ?? [])

      return annotations.filter((annotation) => {
        if (!Number.isInteger(annotation.pageNumber) || annotation.pageNumber <= 0) return false
        if (deletedPageNumbers.has(annotation.pageNumber)) return false
        if (typeof doc.pageCount == 'number' && annotation.pageNumber > doc.pageCount) return false
        return true
      })
    }
  }
}

function optimisticEntity<T>(params: {
  base: T
  queueItems: RequestQueueItem[]
  filter: (req: FolderRequest) => boolean
  reduce: (entity: T, req: FolderRequest) => T
}): T {
  const { base, queueItems, filter, reduce } = params

  const pending = queueItems
    .filter(isOptimisticOperationItem)
    .map((i) => i.op.request)
    .filter(filter)

  if (pending.length == 0) return base
  return pending.reduce(reduce, base)
}

function optimisticDocument(doc: STDocument, req: FolderRequest): STDocument {
  switch (req.type) {
    case 'folders/setDocumentVisibility':
      return doc.visibility == req.visibility ? doc : { ...doc, visibility: req.visibility }
    case 'folders/deleteDocumentPages': {
      const next = req.pageNumbers.reduce((arr, p) => setAdd(arr, p), doc.deletedPageNumbers ?? [])
      return next == (doc.deletedPageNumbers ?? []) ? doc : { ...doc, deletedPageNumbers: next }
    }
    case 'folders/restoreDocumentPages': {
      const next = req.pageNumbers.reduce(
        (arr, p) => setRemove(arr, p),
        doc.deletedPageNumbers ?? []
      )
      return next == (doc.deletedPageNumbers ?? []) ? doc : { ...doc, deletedPageNumbers: next }
    }
    case 'folders/setDocumentPageGroups':
      return { ...doc, pageGroups: req.pageGroups }
    case 'folders/updateDocumentPageRotation': {
      const nextRotations: Record<string, number> = { ...doc.pageRotations }

      if (req.rotation == 0) {
        delete nextRotations[req.pageNumber]
        delete nextRotations[String(req.pageNumber)]
      } else {
        nextRotations[req.pageNumber] = req.rotation
      }

      return { ...doc, pageRotations: nextRotations }
    }
    case 'folders/updateDocumentPageRotations': {
      const nextRotations: Record<string, number> = { ...doc.pageRotations }

      for (const pageNumber of req.pageNumbers) {
        if (req.rotation == 0) {
          delete nextRotations[pageNumber]
          delete nextRotations[String(pageNumber)]
        } else {
          nextRotations[pageNumber] = req.rotation
        }
      }

      return { ...doc, pageRotations: nextRotations }
    }
    default:
      return doc
  }
}

export function selDoc(state: STFolderModuleState, docId: string | undefined) {
  if (!docId || state.status != 'ready') return undefined
  return state.folderState.documents.find((d) => d.id == docId)
}

export function selSuggestedCategoryIds(state: STFolderModuleState) {
  if (state.status != 'ready') return []
  return state.folderState.suggestedCategoryIds
}

export function selHiddenCategoryIds(state: STFolderModuleState) {
  if (state.status != 'ready') return []
  return state.folderState.hiddenCategoryIds
}

export function selMissingReasonOptionsListByConstraintId(state: {
  documentTypes: STDocumentType[]
  categories: STCategory[]
}): Record<string, STMissingReason[]> {
  const reasonsById: Record<string, STMissingReason[]> = {}

  for (const cat of state.categories) {
    reasonsById[cat.id] = cat.missingReasons
  }
  for (const documentType of state.documentTypes) {
    reasonsById[documentType.id] = documentType.missingReasons
  }

  return reasonsById
}

export function selUploadedDocuments(state: STFolderModuleState): STDocument[] {
  if (state.status != 'ready') return []
  return state.folderState.documents.filter((doc) => doc.documentTypeId != 'stanfordtax')
}

function reorderDocuments(documents: STDocument[], request: ReorderDocument): STDocument[] {
  if (request.documentId == request.referenceDocumentId) {
    return documents
  }

  const itemIndex = documents.findIndex((doc) => doc.id == request.documentId)
  const referenceIndex = documents.findIndex((doc) => doc.id == request.referenceDocumentId)

  if (itemIndex == -1 || referenceIndex == -1) {
    return documents
  }

  const movingDocument = documents[itemIndex]
  const referenceDocument = documents[referenceIndex]

  if (
    movingDocument.documentTypeId != referenceDocument.documentTypeId ||
    movingDocument.documentTypeId == 'stanfordtax'
  ) {
    return documents
  }

  const reorderedDocuments = [...documents]
  const [documentToMove] = reorderedDocuments.splice(itemIndex, 1)
  const nextReferenceIndex = reorderedDocuments.findIndex(
    (doc) => doc.id == request.referenceDocumentId
  )

  if (nextReferenceIndex == -1) {
    return documents
  }

  const targetIndex = request.relation == 'before' ? nextReferenceIndex : nextReferenceIndex + 1
  reorderedDocuments.splice(targetIndex, 0, documentToMove)
  return reorderedDocuments
}

export function selDocumentSignOffAnnotations(doc: STDocument) {
  const annotations = Object.values(doc.annotations).filter(
    (a): a is StampAnnotation => a.type == 'stamp' && a.stampType == 'sign_off'
  )
  return sort(annotations, rank('stampCode', ['prepared', 'reviewed', 'reviewed2']))
}

export function selDocumentTooltip(
  doc: STDocument,
  context: {
    memberships: ScopedFolderMembership[]
    signOffAnnotations: StampAnnotation[]
    warnings: DocumentWarning[]
    documentNameById?: Record<string, string>
  }
) {
  if (!doc.uploadedAt) return undefined

  const uploaderSide = doc.uploadedBy
    ? folderMembershipSide(context.memberships, doc.uploadedBy)
    : undefined

  const byExtra = (() => {
    if (uploaderSide == 'firm') return ' by firm '
    if (uploaderSide == 'client') return ' by client '
    return ''
  })()

  const status = ['Uploaded', byExtra, 'at ' + formatISO8601DateTime(doc.uploadedAt)]
    .filter(isNotEmpty)
    .join(' ')

  const lines = [
    doc.name,
    status,
    ...context.signOffAnnotations.map((a) => formatSignOffAnnotation(a, context.memberships)),
    ...context.warnings.map((warning) =>
      formatDocumentWarning(warning, { documentNameById: context.documentNameById })
    )
  ]

  return lines.join('\n')
}

function formatSignOffAnnotation(signOff: StampAnnotation, memberships: ScopedFolderMembership[]) {
  const user = memberships.find((m) => m.user.id == signOff.authorId)?.user
  const actor = user?.name ?? user?.email ?? 'firm'
  return `${formatStampCode(signOff.stampCode)} by ${actor} at ${formatISO8601DateTime(
    signOff.time
  )}`
}

function formatStampCode(code: string | undefined) {
  switch (code) {
    case 'prepared':
      return 'Prepared'
    case 'reviewed':
      return 'Reviewed'
    case 'reviewed2':
      return 'Reviewed'
    default:
      return code
  }
}

const IDLE_DASHBOARD: FolderQuestionnaireDashboardState = { status: 'idle' }

export function selQuestionnaireDashboard(
  state: STFolderModuleState
): FolderQuestionnaireDashboardState {
  if (state.status != 'ready' || state.questionnaire.status != 'ready') return IDLE_DASHBOARD
  return state.questionnaire.dashboard
}

export function orderedOrganizerSections(settings: ClientOrganizerSettings) {
  return Object.entries(settings.sections).sort(([, a], [, b]) => a.order - b.order)
}

/** The firm's invoice link, whether submission waits for it, and when it was marked paid. */
export function folderInvoice(section: STOpenFolderState['payInvoice'] | null) {
  return match(section)
    .with(
      { embeddedResource: { paymentMethod: { type: 'payment_link', url: P.string } } },
      (section) => ({
        url: section.embeddedResource.paymentMethod.url,
        required: section.requiredBy.includes('tax_return_delivery'),
        status: section.embeddedResource.status,
        paidAt: section.embeddedResource.paidAt ?? undefined
      })
    )
    .otherwise(() => undefined)
}

export function invoicePaidLabel(paidAt: string) {
  return `Paid on ${formatISO8601Date(paidAt)} at ${formatISO8601Time(paidAt)}`
}

/** The firm's dialog: the unsaved link and submission requirement. */
export function selPaymentLinkDialog(state: STFolderModuleState) {
  if (state.status != 'ready' || !state.paymentLinkDialog) return undefined
  const draft = state.paymentLinkDialog
  const invoice = folderInvoice(state.folderState.payInvoice)
  return {
    ...draft,
    title: match(invoice)
      .with(undefined, () => 'Add invoice link')
      .otherwise(() => 'Edit invoice link'),
    canRemove: invoice != undefined,
    canSave: isValidInvoiceUrl(draft.url)
  }
}

export function checklistViewerModel(state: STFolderReadyState) {
  const documents = selFolderDocuments(state)
  const reasons = selFolderMissingReasonOptionsByConstraintId(state)
  const pages = paginateChecklistItems(selChecklistItems(state)).map((page) => ({
    ...page,
    rows: checklistRows(page.items, documents, reasons)
  }))
  return {
    pagesByNumber: Object.fromEntries(pages.map((page) => [page.number, page])),
    documentTypes: selDocumentTypeOptions(state),
    reasons,
    sections: selNonStanfordtaxBookmarkSections(state)
  }
}
